import { easeInOut, easeOut, motion } from 'motion/react'
import { ExternalLink, Puzzle } from 'lucide-react'
import { Seo } from '~/components/Seo'
import { PageLayout } from '~/lib/components/layouts/PageLayout'

const tools = [
  {
    icon: Puzzle,
    name: 'Article Selection Navigator',
    description:
      'Include and exclude articles without leaving your browser, then export the selection as Excel or CSV. PRISMA conform, free, and it stores no personal data.',
    link: 'https://chromewebstore.google.com/detail/article-selection-navigat/pgmiljfohpamophnnmhnfjncehonmpid',
    external: true,
    cta: 'Add to Chrome, free',
  },
]

const floatingAnimation = {
  initial: { y: 0 },
  animate: {
    y: [-12, 12, -12],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

const glowAnimation = {
  initial: { opacity: 0.3 },
  animate: {
    opacity: [0.3, 0.6, 0.3],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

const Index = () => (
  <>
    <Seo
      title="Free Article Screening Tool for Literature Reviews | SOTA Navigator"
      description="Article Selection Navigator is a free Chrome and Edge extension: include and exclude articles in your browser, PRISMA conform, and export the selection to Excel or CSV."
    />

    <PageLayout>
      <div className="relative overflow-hidden bg-[#001871] text-white">
        {/* Enhanced texture overlay */}
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
        <div className="absolute inset-0 bg-linear-to-b from-[#001871] via-[#001871] to-[#001871]/90"></div>

        {/* Abstract shapes with improved contrast */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.15, scale: 1 }}
          transition={{ duration: 2 }}
          className="absolute left-20 top-20 size-96 rounded-full bg-white blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.15, scale: 1 }}
          transition={{ duration: 2, delay: 0.5 }}
          className="absolute bottom-20 right-20 size-96 rounded-full bg-white blur-3xl"
        />

        {/* Floating elements */}
        <motion.div
          variants={floatingAnimation}
          initial="initial"
          animate="animate"
          className="absolute left-20 top-40 hidden size-20 rounded-2xl bg-white/20 shadow-lg backdrop-blur-lg lg:block"
        />
        <motion.div
          variants={floatingAnimation}
          initial="initial"
          animate="animate"
          transition={{ delay: 1 }}
          className="absolute bottom-40 right-20 hidden size-16 rounded-full bg-white/20 shadow-lg backdrop-blur-lg lg:block"
        />

        <div className="relative py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-4xl text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: easeOut }}
                className="space-y-8"
              >
                <motion.div
                  variants={glowAnimation}
                  initial="initial"
                  animate="animate"
                  className="inline-flex items-center rounded-full bg-white/20 px-6 py-2 text-sm font-medium text-white shadow-lg backdrop-blur-xs"
                >
                  Free tool
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                  className="text-5xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-7xl"
                >
                  Screen your articles without leaving the browser
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                  className="mx-auto max-w-3xl text-xl text-white/80"
                >
                  One tool, free, no sign-up. Built for medical device literature reviews, and it
                  keeps your PRISMA counts straight while you work.
                </motion.p>
              </motion.div>
            </div>

            <div className="mx-auto mt-24 grid max-w-xl grid-cols-1 gap-8">
              {tools.map((tool, index) => (
                <motion.a
                  key={tool.name}
                  href={tool.link}
                  {...(tool.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group overflow-hidden rounded-2xl bg-white/10 p-8 shadow-lg backdrop-blur-xs transition-all hover:-translate-y-1 hover:shadow-2xl"
                >
                  <div className="mb-6 flex items-center justify-between gap-4">
                    <div className="inline-block rounded-xl bg-white/20 p-4 transition-colors group-hover:bg-white/30">
                      <tool.icon className="size-8 text-white" />
                    </div>
                    {tool.external ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-white">
                        <ExternalLink className="size-3" aria-hidden="true" />
                        Browser extension
                      </span>
                    ) : null}
                  </div>
                  <h3 className="mb-4 text-2xl font-bold text-white">{tool.name}</h3>
                  <p className="mb-6 text-white/70">{tool.description}</p>
                  <span className="inline-flex items-center font-medium text-white group-hover:underline">
                    {tool.cta ?? 'Try Now'}
                    <svg
                      className="ml-2 size-4 transition-transform group-hover:translate-x-1"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14m-7-7l7 7-7 7" />
                    </svg>
                  </span>
                </motion.a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  </>
)

export default Index
